import MagnifyingGlassIcon from '@heroicons/react/24/outline/MagnifyingGlassIcon';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import React, {
    ComponentPropsWithoutRef,
    ComponentType,
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

import { ButtonPanel } from '@/components/core/ButtonPanel/ButtonPanel';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { Icon } from '../Icon/Icon';

export interface SearchInputProps<T>
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        'onChange' | 'size'
    > {
    searchResults?: T[];
    renderResult?: (item: T, index: number) => React.ReactNode;
    getResultKey?: (item: T, index: number) => string;
    isResultDisabled?: (item: T) => boolean;
    /**
     * Optional per-result HTML attributes spread onto the `<li>` row.
     * Use this when callers need `data-*` selectors on the same DOM
     * element that carries `data-testid="search-element"` (e.g. existing
     * Cucumber/Capybara selectors). Returned values are spread BEFORE
     * SearchInput's own props so reserved attributes (`data-testid`,
     * `role`, `id`, click handlers, etc.) cannot be overridden.
     */
    getResultAttributes?: (item: T, index: number) => Record<string, string | undefined>;
    maxHeight?: string;
    containerClassName?: string;
    resultsClassName?: string;
    /** Extra classes for the trigger button (e.g. to showcase pseudo states). */
    triggerClassName?: string;
    /** Render the input expanded on first mount (used by stories/tests). */
    defaultOpen?: boolean;
    value?: string;
    onResultClick?: (result: T) => void;
    onClear?: () => void;
    onChange?: (value: string) => void;
}

interface ListboxPosition {
    top: number;
    left: number;
    width: number;
}

const FOCUS_DELAY_MS = 100;

/** Distance between the input field's bottom edge and the dropdown top. */
const DROPDOWN_OFFSET_PX = 4;

/**
 * Gap (px) the slide-out input keeps to the left of its own trigger button.
 * Matches the `tw-gap-1` (4px) spacing from the Figma "SearchInput" component.
 * The input only ever reserves space for its OWN trigger — any spacing for
 * sibling buttons (e.g. a settings/burger button) is owned by the parent
 * layout, so SearchInput can be dropped in anywhere without a phantom gap.
 */
const INPUT_TRIGGER_GAP_PX = 4;

/**
 * One of the two stacked heroicons inside the trigger button. The icon
 * cross-fades + rotates + scales when its `visible` flag flips, matching
 * the 150 ms animation from the Figma Make file.
 *
 * @param hiddenRotation Direction the icon rotates while hidden — `cw`
 *                       (clockwise, +90°) or `ccw` (counter-clockwise, −90°).
 *                       Using opposite rotations on the two icons creates
 *                       the circular-swap effect from the Figma animation.
 */
const MorphingTriggerIcon = ({
    icon,
    visible,
    hiddenRotation,
    disabled,
}: {
    icon: ComponentType<ComponentPropsWithoutRef<'svg'>>;
    visible: boolean;
    hiddenRotation: 'cw' | 'ccw';
    disabled?: boolean;
}) => (
    <Icon
        icon={icon}
        size="sm"
        className={cn(
            disabled ? 'tw-icon-inactive' : 'tw-icon-primary',
            'tw-absolute tw-inset-0 tw-w-full tw-h-full',
            'tw-transition-all tw-duration-150 tw-ease-in-out',
            visible
                ? 'tw-opacity-100 tw-rotate-0 tw-scale-100'
                : cn(
                    'tw-opacity-0 tw-scale-50 tw-pointer-events-none',
                    hiddenRotation === 'cw' ? 'tw-rotate-90' : '-tw-rotate-90',
                ),
        )}
    />
);

function SearchInputImpl<T>(
    {
        className,
        containerClassName,
        resultsClassName,
        triggerClassName,
        defaultOpen = false,
        onClear,
        searchResults = [],
        renderResult,
        getResultKey,
        isResultDisabled,
        getResultAttributes,
        maxHeight = 'tw-max-h-72',
        onChange,
        onResultClick,
        value,
        disabled,
        ...inputProps
    }: SearchInputProps<T>,
    forwardedRef: React.ForwardedRef<HTMLInputElement>,
): React.ReactElement {
    const { t } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [listboxPosition, setListboxPosition] = useState<ListboxPosition | null>(null);
    const [inputRightPx, setInputRightPx] = useState<number | null>(null);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const internalInputRef = useRef<HTMLInputElement | null>(null);
    const inputShellRef = useRef<HTMLDivElement | null>(null);
    const listboxRef = useRef<HTMLUListElement | null>(null);

    const reactId = useId();
    const listboxId = `${reactId}-listbox`;
    const optionIdPrefix = `${reactId}-option`;

    const showResults = open && searchResults.length > 0;
    const activeOptionId =
        showResults && activeIndex >= 0 ? `${optionIdPrefix}-${activeIndex}` : undefined;

    const setInputRef = useCallback(
        (node: HTMLInputElement | null) => {
            internalInputRef.current = node;
            if (typeof forwardedRef === 'function') {
                forwardedRef(node);
            } else if (forwardedRef) {
                forwardedRef.current = node;
            }
        },
        [forwardedRef],
    );

    const close = useCallback(() => {
        setOpen(false);
        onChange?.('');
        onClear?.();
    }, [onChange, onClear]);

    useEffect(() => {
        if (!open) return;
        const id = window.setTimeout(() => {
            internalInputRef.current?.focus({ preventScroll: true });
        }, FOCUS_DELAY_MS);
        return () => window.clearTimeout(id);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (containerRef.current?.contains(target)) return;
            if (listboxRef.current?.contains(target)) return;
            close();
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [open, close]);

    useEffect(() => {
        if (!open) setActiveIndex(-1);
    }, [open, searchResults.length]);

    useLayoutEffect(() => {
        if (!showResults) {
            setListboxPosition(null);
            return;
        }

        const anchor = inputShellRef.current;
        if (!anchor) return;

        const updatePosition = () => {
            const rect = anchor.getBoundingClientRect();
            setListboxPosition({
                top: rect.bottom + DROPDOWN_OFFSET_PX,
                left: rect.left,
                width: rect.width,
            });
        };

        updatePosition();

        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        let resizeObserver: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => updatePosition());
            resizeObserver.observe(anchor);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            resizeObserver?.disconnect();
        };
    }, [showResults, searchResults.length]);

    useLayoutEffect(() => {
        const shell = inputShellRef.current;
        const trigger = triggerRef.current;
        if (!shell || !trigger) return;

        const updateRight = () => {
            const reference = (shell.offsetParent as HTMLElement | null) ?? shell.parentElement;
            if (!reference) return;
            const referenceRect = reference.getBoundingClientRect();
            const triggerRect = trigger.getBoundingClientRect();
            const right = referenceRect.right - triggerRect.left + INPUT_TRIGGER_GAP_PX;
            setInputRightPx(Math.max(right, 0));
        };

        updateRight();

        window.addEventListener('resize', updateRight);

        let resizeObserver: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => updateRight());
            resizeObserver.observe(trigger);
            const reference = shell.offsetParent ?? shell.parentElement;
            if (reference) resizeObserver.observe(reference);
        }

        return () => {
            window.removeEventListener('resize', updateRight);
            resizeObserver?.disconnect();
        };
    }, [open]);

    const handleTriggerClick = () => {
        if (disabled) return;
        if (open) {
            close();
            triggerRef.current?.focus();
        } else {
            setOpen(true);
        }
    };

    const findNextEnabled = (start: number, direction: 1 | -1): number => {
        const count = searchResults.length;
        if (count === 0) return -1;
        let i = start;
        for (let step = 0; step < count; step++) {
            i = (i + direction + count) % count;
            if (!isResultDisabled?.(searchResults[i])) return i;
        }
        return -1;
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const count = searchResults.length;
        switch (event.key) {
            case 'Escape':
                event.preventDefault();
                close();
                triggerRef.current?.focus();
                break;
            case 'ArrowDown':
                if (count === 0) return;
                event.preventDefault();
                setActiveIndex((current) => findNextEnabled(current, 1));
                break;
            case 'ArrowUp':
                if (count === 0) return;
                event.preventDefault();
                setActiveIndex((current) =>
                    findNextEnabled(current === -1 ? 0 : current, -1),
                );
                break;
            case 'Home':
                if (count === 0) return;
                event.preventDefault();
                setActiveIndex(findNextEnabled(-1, 1));
                break;
            case 'End':
                if (count === 0) return;
                event.preventDefault();
                setActiveIndex(findNextEnabled(0, -1));
                break;
            case 'Enter':
                if (activeIndex >= 0 && activeIndex < count) {
                    const result = searchResults[activeIndex];
                    if (!isResultDisabled?.(result)) {
                        event.preventDefault();
                        onResultClick?.(result);
                        close();
                    }
                }
                break;
        }
    };

    const handleResultClick = (result: T) => {
        if (isResultDisabled?.(result)) return;
        onResultClick?.(result);
        close();
    };

    // default rendering helpers (used when callers don't override)
    const defaultRender = (item: T): React.ReactNode => {
        if (item && typeof item === 'object' && 'label' in (item as Record<string, unknown>)) {
            const labels = item as { label?: unknown; translatedLabel?: unknown };
            return String(labels.translatedLabel ?? labels.label ?? '');
        }
        return String(item);
    };

    const resolveKey = (item: T, index: number): string => {
        if (getResultKey) return getResultKey(item, index);
        if (item && typeof item === 'object' && 'name' in (item as Record<string, unknown>)) {
            return String((item as unknown as { name: unknown }).name);
        }
        return String(index);
    };

    return (
        <div
            ref={containerRef}
            className={cn(
                'tw-relative tw-flex tw-items-center tw-justify-end',
                containerClassName,
            )}
        >
            <div
                ref={inputShellRef}
                className={cn(
                    'tw-absolute tw-h-9 lg:tw-h-10',
                    'lg:tw-top-1/2 lg:-tw-translate-y-1/2',
                    // Reserve only the trigger's own width (+gap) by default; the
                    // measured value below anchors precisely to the live trigger.
                    'tw-left-0 tw-right-[44px] lg:tw-right-[48px]',
                    'tw-z-30 tw-overflow-hidden tw-pointer-events-none',
                )}
                style={inputRightPx !== null ? { right: `${inputRightPx}px` } : undefined}
                aria-hidden={!open}
            >
                <div
                    data-state={open ? 'open' : 'closed'}
                    className={cn(
                        'tw-flex tw-items-center tw-h-full tw-w-full tw-bg-fill-primary tw-rounded',
                        'tw-px-3 tw-py-2 tw-pointer-events-auto',
                        'tw-transition-transform tw-duration-300 tw-ease-in-out',
                        'tw-border tw-border-solid tw-border-primary-active',
                        open ? 'tw-translate-x-0' : 'tw-translate-x-full',
                    )}
                >
                    <input
                        type="search"
                        ref={setInputRef}
                        role="combobox"
                        aria-controls={listboxId}
                        aria-expanded={showResults}
                        aria-autocomplete="list"
                        aria-activedescendant={activeOptionId}
                        disabled={disabled}
                        tabIndex={open && !disabled ? 0 : -1}
                        className={cn(
                            'tw-w-full tw-h-full tw-bg-transparent tw-border-0 tw-outline-none',
                            'tw-text-t-sm lg:tw-text-t-base tw-text-t-primary placeholder:tw-text-t-inactive',
                            'tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap',
                            'placeholder:tw-overflow-hidden placeholder:tw-text-ellipsis placeholder:tw-whitespace-nowrap',
                            'disabled:tw-cursor-not-allowed disabled:tw-text-t-inactive',
                            // Hide the browser-native clear (×) button that
                            // WebKit/Chrome render inside `<input type="search">`
                            // when it has a value — the SearchInput already
                            // exposes its own X trigger via ButtonPanel, so
                            // the native one is redundant and off-design.
                            '[&::-webkit-search-cancel-button]:tw-appearance-none',
                            '[&::-webkit-search-decoration]:tw-appearance-none',
                            className,
                        )}
                        value={value ?? ''}
                        onChange={(event) => onChange?.(event.target.value)}
                        onKeyDown={handleInputKeyDown}
                        {...inputProps}
                    />
                </div>
            </div>
            <ButtonPanel
                ref={triggerRef}
                isDisabled={!!disabled}
                onClick={handleTriggerClick}
                data-testid="search-input-toggle"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-label={open ? t('CLOSE') : t('SEARCH')}
                className={cn('tw-relative tw-z-40', triggerClassName)}
            >
                <span className="tw-relative tw-block tw-w-5 tw-h-5 lg:tw-w-6 lg:tw-h-6">
                    <MorphingTriggerIcon
                        icon={MagnifyingGlassIcon}
                        visible={!open}
                        hiddenRotation="cw"
                        disabled={disabled}
                    />
                    <MorphingTriggerIcon
                        icon={XMarkIcon}
                        visible={open}
                        hiddenRotation="ccw"
                        disabled={disabled}
                    />
                </span>
            </ButtonPanel>

            {showResults && listboxPosition && typeof document !== 'undefined' &&
                createPortal(
                    <ul
                        ref={listboxRef}
                        id={listboxId}
                        role="listbox"
                        style={{
                            position: 'fixed',
                            top: `${listboxPosition.top}px`,
                            left: `${listboxPosition.left}px`,
                            width: `${listboxPosition.width}px`,
                        }}
                        className={cn(
                            'tw-bg-fill-primary tw-shadow-lg',
                            'tw-border tw-border-primary tw-rounded',
                            'tw-overflow-y-auto tw-list-none tw-p-0 tw-m-0',
                            'tw-z-[9999]',
                            maxHeight,
                            resultsClassName,
                        )}
                    >
                        {searchResults.map((result, index) => {
                            const disabled = isResultDisabled?.(result) ?? false;
                            const optionId = `${optionIdPrefix}-${index}`;
                            const isActive = index === activeIndex;
                            return (
                                <li
                                    key={resolveKey(result, index)}
                                    {...(getResultAttributes?.(result, index) ?? {})}
                                    id={optionId}
                                    role="option"
                                    aria-selected={isActive}
                                    aria-disabled={disabled || undefined}
                                    data-testid="search-element"
                                    data-active={isActive ? 'true' : 'false'}
                                    data-disabled={disabled ? 'true' : 'false'}
                                    onMouseEnter={() => !disabled && setActiveIndex(index)}
                                    onClick={() => handleResultClick(result)}
                                    className={cn(
                                        'tw-px-4 tw-py-2 tw-text-t-sm',
                                        disabled
                                            ? 'tw-cursor-default tw-pointer-events-none'
                                            : 'tw-cursor-pointer',
                                        !disabled && isActive && 'tw-bg-gray-100',
                                        !disabled && !isActive && 'hover:tw-bg-gray-100',
                                    )}
                                >
                                    {renderResult
                                        ? renderResult(result, index)
                                        : defaultRender(result)}
                                </li>
                            );
                        })}
                    </ul>,
                    document.body,
                )}
        </div>
    );
}

const SearchInput = forwardRef(SearchInputImpl) as <T>(
    props: SearchInputProps<T> & React.RefAttributes<HTMLInputElement>,
) => React.ReactElement;

(SearchInput as unknown as { displayName: string }).displayName = 'SearchInput';

export { SearchInput };
